import { defineStore } from 'pinia';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { ActiveStudyData, SerializedStudyData } from './study-data-class';

interface ActivePatient {
  studyKey: string;
  name?: string;
}

export const useActiveStudyStore = defineStore('active-study', {
  state: () => ({
    activePatient: null as ActivePatient | null,
    volumeKey: null as string | null,
    studyKey: null as string | null,
    studyData: null as ActiveStudyData | null,
    hasSavedState: true, // By default true, set to false when changes are made (contours).
    // Set to true when state is saved.
    // Also set to true when state is loaded.
  }),
  actions: {
    initStudyData() {
      this.studyData = new ActiveStudyData();
    },

    addStudyData(seriesIdxAsString: string, seriesIndex: number, sliceIndex: number, numPhases: number) {
      this.studyData?.addDefaultSeries(
        seriesIdxAsString,
        seriesIndex,
        sliceIndex,
        numPhases,
      );
    },

    getBreathholdState(seriesIdxAsString?: string, sliceIndex?: number) {
      if (
        seriesIdxAsString === undefined
        || seriesIdxAsString === null
        || sliceIndex === undefined
        || sliceIndex === null
        || this.studyData === null
      ) {
        return null;
      }

      const studyObject = this.getStudyObject(seriesIdxAsString, sliceIndex);
      const breathhold = studyObject?.getBreathhold();
      return breathhold;
    },

    serializeStudyData() {
      if (this.studyData === null) return null;
      return this.studyData.serialize();
    },

    deserializeStudyData(serializedData: SerializedStudyData) {
      if (serializedData === null || serializedData === undefined) {
        return;
      }

      if (!this.studyData) {
        this.initStudyData();
      }
      if (this.studyData)
        this.studyData.deserialize(serializedData);
    },

    getStudyStates(seriesIdxAsString?: string, sliceIndex?: number) {
      const studyObject = this.getStudyObject(seriesIdxAsString, sliceIndex);
      const allStates = studyObject?.getStates();
      return allStates;
    },

    getStudyObject(seriesIdxAsString?: string, sliceIndex?: number) {
      if (
        seriesIdxAsString === undefined
        || seriesIdxAsString === null
        || sliceIndex === undefined
        || sliceIndex === null
        || this.studyData === null
        || sliceIndex < 0
      ) {
        return null;
      }
      return this.studyData?.getSliceObjectByIndx(
        seriesIdxAsString,
        sliceIndex,
      );
    },
    getStudyStatebyIndex(seriesIdxAsString?: string, sliceIndex?: number, imageIndex?: number) {
      if (
        imageIndex === undefined
        || imageIndex === null
      ) {
        return null;
      }

      const studyObject = this.getStudyObject(seriesIdxAsString, sliceIndex);
      const state = studyObject?.getImageState(imageIndex);

      return state;
    },
    setActive(studyInstanceUid: string) {
      this.activePatient = { studyKey: studyInstanceUid };
    },

    setPatientInfo(studyKey: string) {
      const currentStudyStore = useCurrentStudyStore();
      if (this.activePatient) {
        this.activePatient.name
          = currentStudyStore.studyInfoDicom.PatientName;
      }
      this.volumeKey = studyKey;
      this.studyKey = studyKey;
    },
  },
});
